import {
  Component, ElementRef, HostListener, Input, ViewEncapsulation, inject, signal,
} from '@angular/core';
import { Router } from '@angular/router';
import { CustomerSessionService } from '../../core/customer/customer-session.service';
import { CustomerProfileStore } from '../../core/customer/customer-profile.store';
import { loginCommands } from '../../core/customer/return-url.util';
import { FulfilmentService } from '../../core/delivery/fulfilment.service';

/**
 * TC-A1b — the chip's visibility rule, exported so the header can ask whether
 * it would carry anything without duplicating the gate (a copy in two files is
 * how the bar ended up rendering empty in the first place).
 *
 * Customer accounts are a delivery-only feature, so a dine-in table token or a
 * pickup-only store shows neither "Sign in" nor an avatar.
 */
export function accountChipVisible(
  publicToken: string | null, deliveryEnabled: boolean): boolean {
  return !!publicToken && deliveryEnabled;
}

/**
 * TC-A1/TC-A2 — account affordance for the global header.
 *  • Anonymous → a "Sign in" pill that routes to the guard-free /login carrying
 *    a returnUrl, so sign-in lands back on the current screen.
 *  • Signed in → a 44×44 initials avatar that opens a dropdown account menu
 *    (Profile · Addresses · Orders · Sign out). Sign out clears the session and
 *    STAYS on the current screen (leaving an account/* screen for the landing,
 *    since that would otherwise be guarded). Hidden entirely when token-less.
 * Self-navigates so host screens stay dumb. Logical CSS props + theme tokens.
 */
@Component({
  selector: 'tc-account-chip',
  standalone: true,
  encapsulation: ViewEncapsulation.None,
  template: `
    @if (chipVisible) {
      @if (session.isAuthenticated()) {
        <div class="tc-acct" [class.tc-acct--open]="open()">
          <button type="button" class="tc-acct-chip" (click)="toggle()"
                  aria-haspopup="menu" [attr.aria-expanded]="open()"
                  [attr.aria-label]="'Account menu, ' + profile.name()">
            <span class="tc-acct-chip__initials" aria-hidden="true">{{ profile.initials() }}</span>
          </button>

          @if (open()) {
            <div class="tc-acct-menu" role="menu" aria-label="Account">
              <div class="tc-acct-menu__head">
                <div class="tc-acct-menu__name">{{ profile.name() }}</div>
                @if (profile.contact()) {
                  <div class="tc-acct-menu__contact">{{ profile.contact() }}</div>
                }
              </div>
              <button type="button" role="menuitem" class="tc-acct-menu__item" (click)="navTo('profile')">Profile</button>
              <button type="button" role="menuitem" class="tc-acct-menu__item" (click)="navTo('addresses')">Saved addresses</button>
              <button type="button" role="menuitem" class="tc-acct-menu__item" (click)="navTo('orders')">Orders</button>
              <button type="button" role="menuitem" class="tc-acct-menu__item tc-acct-menu__item--danger" (click)="signOut()">Sign out</button>
            </div>
          }
        </div>
      } @else {
        <button type="button" class="tc-acct-chip tc-acct-chip--signin" (click)="signIn()"
                aria-label="Sign in">
          <span aria-hidden="true">Sign in</span>
        </button>
      }
    }
  `,
  styles: [`
    .tc-acct { position: relative; display: inline-flex; }
    .tc-acct-chip {
      inline-size: 44px; block-size: 44px; border-radius: 999px;
      display: grid; place-items: center;
      background: var(--tc-primary-soft); color: var(--tc-primary);
      border: 1px solid var(--tc-primary-soft);
      font: inherit; font-weight: 800; font-size: 15px; letter-spacing: -.02em;
      cursor: pointer; -webkit-tap-highlight-color: transparent;
      transition: background-color .15s, color .15s, border-color .15s, transform .08s;
    }
    .tc-acct-chip:hover { background: var(--tc-primary); color: #fff; border-color: var(--tc-primary); }
    .tc-acct-chip:active { transform: scale(0.96); }
    .tc-acct-chip:focus-visible { outline: none; box-shadow: 0 0 0 3px var(--tc-primary-soft); }
    .tc-acct-chip--signin {
      inline-size: auto; block-size: 44px; padding-inline: 16px; border-radius: 999px;
      font-size: 13px; font-weight: 700; white-space: nowrap;
    }
    .tc-acct-menu {
      position: absolute; inset-block-start: calc(100% + 8px); inset-inline-end: 0;
      z-index: 40; min-inline-size: 220px;
      background: #fff; border: 1px solid var(--tc-border); border-radius: var(--tc-radius-card);
      box-shadow: var(--tc-shadow-soft); padding: 6px;
      display: flex; flex-direction: column;
    }
    .tc-acct-menu__head { padding: 10px 12px 8px; border-block-end: 1px solid var(--tc-border); margin-block-end: 4px; }
    .tc-acct-menu__name { font-weight: 800; font-size: 14px; color: var(--tc-fg);
      white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
    .tc-acct-menu__contact { font-size: 12.5px; color: var(--tc-muted); margin-block-start: 2px; }
    .tc-acct-menu__item {
      appearance: none; border: 0; background: transparent; font: inherit;
      text-align: start; padding: 11px 12px; border-radius: 10px; cursor: pointer;
      font-size: 14px; font-weight: 600; color: var(--tc-fg);
    }
    .tc-acct-menu__item:hover { background: var(--tc-bg-soft); }
    .tc-acct-menu__item:focus-visible { outline: none; box-shadow: 0 0 0 3px var(--tc-primary-soft); }
    .tc-acct-menu__item--danger { color: var(--tc-error-fg); }
  `],
})
export class TcAccountChipComponent {
  /** The active store token; the chip is hidden when null. */
  @Input({ required: true }) publicToken: string | null = null;

  readonly session = inject(CustomerSessionService);
  readonly profile = inject(CustomerProfileStore);
  /** #4 — customer accounts (profile / addresses / orders) are a delivery-only
   *  feature, and sign-in 403s on a delivery-off store. Hide the whole chip
   *  (Sign in + avatar) unless delivery is enabled. */
  readonly fulfilment = inject(FulfilmentService);
  private router = inject(Router);
  private host = inject(ElementRef<HTMLElement>);

  readonly open = signal(false);

  /** TC-A1b — single source of truth, shared with the header's own gate. */
  get chipVisible(): boolean {
    return accountChipVisible(this.publicToken, this.fulfilment.deliveryEnabled());
  }

  toggle(): void { this.open.update(v => !v); }
  private close(): void { this.open.set(false); }

  /** Anonymous → guard-free /login carrying the current location as returnUrl. */
  signIn(): void {
    if (!this.publicToken) return;
    this.router.navigate(loginCommands(this.publicToken), { queryParams: { returnUrl: this.router.url } });
  }

  navTo(section: 'profile' | 'addresses' | 'orders'): void {
    this.close();
    if (this.publicToken) this.router.navigate(['/t', this.publicToken, 'account', section]);
  }

  /** Sign out + STAY on the current screen (TC-A2). Only leave when on an
   *  account/* route, which would otherwise be guarded after logout. */
  signOut(): void {
    const token = this.publicToken;
    this.close();
    if (!token) return;
    this.session.clear(token);
    this.profile.reset();
    if (this.router.url.includes('/account')) {
      this.router.navigate(['/t', token]);
    }
  }

  @HostListener('document:click', ['$event'])
  onDocClick(e: MouseEvent): void {
    if (this.open() && !this.host.nativeElement.contains(e.target as Node)) this.close();
  }

  @HostListener('document:keydown.escape')
  onEsc(): void { if (this.open()) this.close(); }
}
